import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import { voucherService, generateIdempotencyKey } from '../services/api';
import { Voucher, VoucherPaymentRequest } from '../types/voucher';

const VoucherPayment: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [voucherNumber, setVoucherNumber] = useState('');
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [paymentData, setPaymentData] = useState<VoucherPaymentRequest>({
    retailerId: '',
    storeId: '',
    retailerPaymentRef: '',
    totalAmount: 0,
    retailerCommissionAmount: 0,
    currency: 'USD',
  });
  const [paymentResult, setPaymentResult] = useState<Voucher | null>(null);

  const steps = ['Voucher Lookup', 'Payment Details', 'Confirmation'];

  const voucherLookupMutation = useMutation(
    (voucherNum: string) => voucherService.getVoucher(voucherNum),
    {
      onSuccess: (data) => {
        // Check if voucher is in AWAITING_PAYMENT status to be payable
        const isPayable = data.voucherStatus === 'AWAITING_PAYMENT' && new Date(data.expiresAt) > new Date();
        if (isPayable) {
          setVoucher(data);
          setPaymentData(prev => ({
            ...prev,
            totalAmount: data.amount,
            currency: data.currency,
          }));
          setActiveStep(1);
        }
      },
    }
  );

  const paymentMutation = useMutation(
    (data: { voucherNumber: string; payment: VoucherPaymentRequest; idempotencyKey: string }) =>
      voucherService.processPayment(data.voucherNumber, data.payment, data.idempotencyKey),
    {
      onSuccess: (data) => {
        setPaymentResult(data);
        setActiveStep(2);
      },
    }
  );

  const handleVoucherLookup = (e: React.FormEvent) => {
    e.preventDefault();
    voucherLookupMutation.mutate(voucherNumber);
  };

  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentData((prev) => ({
      ...prev,
      [name]: name.includes('Amount') ? parseFloat(value) || 0 : value,
    }));
  };

  const handleProcessPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (voucher) {
      const idempotencyKey = generateIdempotencyKey();
      paymentMutation.mutate({
        voucherNumber: voucher.voucherNumber,
        payment: paymentData,
        idempotencyKey,
      });
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setVoucherNumber('');
    setVoucher(null);
    setPaymentData({
      retailerId: '',
      storeId: '',
      retailerPaymentRef: '',
      totalAmount: 0,
      retailerCommissionAmount: 0,
      currency: 'USD',
    });
    setPaymentResult(null);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Process Voucher Payment
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Voucher Lookup */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleVoucherLookup}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Voucher Number"
                  placeholder="Enter voucher number"
                  value={voucherNumber}
                  onChange={(e) => setVoucherNumber(e.target.value)}
                  disabled={voucherLookupMutation.isLoading}
                />
              </Grid>
              {voucherLookupMutation.isError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    Voucher not found or not payable. Please check the voucher number.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={voucherLookupMutation.isLoading || !voucherNumber}
                  startIcon={voucherLookupMutation.isLoading && <CircularProgress size={20} />}
                >
                  {voucherLookupMutation.isLoading ? 'Looking up...' : 'Look Up Voucher'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Step 2: Payment Details */}
      {activeStep === 1 && voucher && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Voucher Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Voucher Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {voucher.voucherNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount Due
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {voucher.currency} {voucher.amount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Product Description
                  </Typography>
                  <Typography variant="body1">
                    {voucher.productDescription}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleProcessPayment}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Retailer ID"
                    name="retailerId"
                    value={paymentData.retailerId}
                    onChange={handlePaymentChange}
                    disabled={paymentMutation.isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Store ID"
                    name="storeId"
                    value={paymentData.storeId}
                    onChange={handlePaymentChange}
                    disabled={paymentMutation.isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Payment Reference"
                    name="retailerPaymentRef"
                    value={paymentData.retailerPaymentRef}
                    onChange={handlePaymentChange}
                    disabled={paymentMutation.isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Commission Amount"
                    name="retailerCommissionAmount"
                    value={paymentData.retailerCommissionAmount}
                    onChange={handlePaymentChange}
                    inputProps={{ step: '0.01', min: '0' }}
                    disabled={paymentMutation.isLoading}
                  />
                </Grid>
                {paymentMutation.isError && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      Failed to process payment. Please try again.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={paymentMutation.isLoading}
                    startIcon={paymentMutation.isLoading && <CircularProgress size={20} />}
                  >
                    {paymentMutation.isLoading ? 'Processing...' : 'Process Payment'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </>
      )}

      {/* Step 3: Confirmation */}
      {activeStep === 2 && paymentResult && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Payment processed successfully!
          </Alert>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Confirmation
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Voucher Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {paymentResult.voucherNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Retailer Payment Ref
                  </Typography>
                  <Typography variant="body1">
                    {paymentResult.retailerPaymentRef || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount Paid
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {paymentResult.currency} {paymentResult.amount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    {paymentResult.voucherStatus.replace(/_/g, ' ')}
                  </Typography>
                </Grid>
              </Grid>
              <Box mt={3}>
                <Button variant="contained" onClick={handleReset}>
                  Process Another Payment
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      )}
    </Box>
  );
};

export default VoucherPayment;